import { useState, useEffect } from 'react';
import { usePaymentHistory } from '../hooks/useTallyQueries';
import { useUIStore } from '../store';
import { LoadingSpinner, ErrorMessage, Button } from './ui';
import { formatCurrency, formatRelativeTimeIST, formatTimestampIST } from '../utils/formatters';
import type { PaymentHistoryTransaction } from '../types';

export const PaymentHistory = () => {
  const { setActiveView } = useUIStore();
  const [limit, setLimit] = useState(10);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  
  const {
    data: historyData,
    isLoading,
    error,
    refetch
  } = usePaymentHistory(limit);

  // Refresh data when component mounts (only if data is stale)
  useEffect(() => {
    console.log('PaymentHistory: Component mounted, checking if refresh is needed...');
    // Only refetch if data is stale or doesn't exist
    if (!historyData || Date.now() - (lastRefreshed?.getTime() || 0) > 30000) { // 30 seconds
      console.log('PaymentHistory: Data is stale, refreshing...');
      refetch().then(() => {
        setLastRefreshed(new Date());
      });
    }
  }, []); // Empty dependency array means this runs only once on mount

  const handleLoadMore = () => {
    setLimit(prev => prev + 10);
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'success':
        return 'bg-green-100 text-green-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };


  const PaymentCard = ({ payment }: { payment: PaymentHistoryTransaction }) => (
    <div className="bg-white rounded-lg border shadow-sm">
      <div className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-1">
              <span className="text-lg">💳</span>
              <span className="font-semibold text-lg text-gray-900">
                {formatCurrency(payment.amount_paid)}
              </span>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(payment.status)}`}>
                {payment.status}
              </span>
            </div>
            
            <p className="text-sm text-gray-600 mb-1">
              {formatTimestampIST(payment.paid_at)}
            </p>
            
            <p className="text-xs text-gray-500 mb-1">
              Transaction ID: {payment.id}
            </p>
            
            <div className="flex items-center space-x-2 text-xs text-gray-500">
              <span>{payment.payment_method.toUpperCase()}</span>
              {payment.bank_name && <span>• {payment.bank_name}</span>}
              {payment.card_type && <span>• {payment.card_type}</span>}
            </div>
          </div>
          
          <div className="text-right">
            <p className="text-xs text-gray-500 mb-1">
              {formatRelativeTimeIST(payment.paid_at)}
            </p>
            <div className="space-y-1">
              {/* <span className={`px-2 py-1 rounded-full text-xs font-medium ${getVoucherStatusColor(payment.tally_sync_status)}`}>
                Tally: {payment.tally_sync_status}
              </span> */}
              {payment.voucher_processing_status && (
                <div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    payment.voucher_processing_status === 'COMPLETED' || payment.voucher_processing_status === 'SYNCED' ? 'bg-green-100 text-green-800' :
                    payment.voucher_processing_status === 'FAILED' ? 'bg-red-100 text-red-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    Voucher: {payment.voucher_processing_status}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Charges Breakdown */}
        {payment.charge_allocations && payment.charge_allocations.length > 0 && (
          <div className="border-t pt-3">
            <div className="flex justify-between items-center mb-2">
              <p className="text-sm font-medium text-gray-700">
                Charges Paid ({payment.charge_allocations.length})
              </p>
              <div className="text-xs text-gray-500">
                Amount: {formatCurrency(payment.amount_paid)} + Fee: {formatCurrency(payment.convenience_fee)}
              </div>
            </div>
            <div className="space-y-2">
              {payment.charge_allocations.map((charge, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-2">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <span className="text-sm font-medium text-gray-900">
                        {charge.chargeName || charge.charge_name || 'Charge Payment'}
                      </span>
                      <div className="text-xs text-gray-500 mt-1">
                        <span>{charge.category}</span>
                        {charge.isPartial && (
                          <span className="ml-2 px-1.5 py-0.5 bg-yellow-100 text-yellow-800 rounded text-xs">
                            Partial Payment
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="text-sm font-medium">
                        {formatCurrency(charge.allocatedAmount)}
                      </span>
                      {charge.isPartial && (
                        <div className="text-xs text-gray-500">
                          of {formatCurrency(charge.fullAmount || 0)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Detailed Tally Sync Info */}
        <div className="border-t pt-3 mt-3">
          <div className="grid grid-cols-2 gap-4 text-xs text-gray-600">
            {payment.voucher_uuid && (
              <div>
                <span className="font-medium">Voucher UUID:</span>
                <div className="font-mono text-xs truncate">{payment.voucher_uuid}</div>
              </div>
            )}
            
            {payment.tally_synced_at && (
              <div>
                <span className="font-medium">Synced At:</span>
                <div>{formatTimestampIST(payment.tally_synced_at)}</div>
              </div>
            )}
            
            {payment.manual_intervention_required && (
              <div className="col-span-2">
                <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded-full text-xs">
                  ⚠️ Manual Intervention Required: Call Amanora Team
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  if (isLoading && !historyData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <LoadingSpinner size="large" className="mx-auto mb-4" />
          <p className="text-gray-600">Loading payment history...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-white shadow-sm border-b">
          <div className="p-4">
            <div className="flex items-center justify-between">
              <Button
                onClick={() => setActiveView('dashboard')}
                variant="outline"
                size="small"
              >
                ← Back
              </Button>
              <h1 className="text-lg font-semibold">Payment History</h1>
              <div className="w-16" />
            </div>
          </div>
        </div>
        
        <div className="p-4">
          <ErrorMessage
            title="Failed to Load History"
            message={error.message}
            action={
              <Button onClick={() => refetch()} variant="outline" size="small">
                Try Again
              </Button>
            }
          />
        </div>
      </div>
    );
  }

  const payments = historyData?.transactions || [];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="p-4">
          <div className="flex items-center justify-between">
            <Button
              onClick={() => setActiveView('dashboard')}
              variant="outline"
              size="small"
            >
              ← Back
            </Button>
            <h1 className="text-lg font-semibold">Payment History</h1>
            <Button
              onClick={() => {
                console.log('PaymentHistory: Manual refresh triggered');
                refetch().then(() => {
                  setLastRefreshed(new Date());
                });
              }}
              variant="outline"
              size="small"
              loading={isLoading}
            >
              🔄 Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      {historyData && (
        <div className="p-4">
          <div className="bg-white rounded-lg border shadow-sm p-4 mb-4">
            <div className="grid grid-cols-2 gap-4 text-center">
              <div>
                <p className="text-2xl font-bold text-green-600">
                  {historyData.totalTransactions}
                </p>
                <p className="text-sm text-gray-600">Total Transactions</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-primary">
                  {formatCurrency(historyData.totalAmountPaid)}
                </p>
                <p className="text-sm text-gray-600">Total Paid</p>
              </div>
            </div>

            {/* Balance Sync Information */}
            {historyData.balanceSync && (
              <div className="mt-3 pt-3 border-t">
                <div className="text-center mb-2">
                  <p className="text-sm font-medium text-gray-700">Balance Sync Status</p>
                </div>
                <div className="grid grid-cols-1 gap-4 text-center">
                  <div>
                    <p className="text-lg font-semibold text-blue-600">
                      {formatCurrency(Math.abs(historyData.totalAmountPaid))}
                    </p>
                    {/* <p className="text-xs text-gray-600">Paid</p> */}
                  </div>
                  {/* <div>
                    <p className="text-lg font-semibold text-blue-600">
                      +{Math.abs(historyData.balanceSync.pendingPaymentCount)}
                    </p>
                    <p className="text-xs text-gray-600">Payments Processed</p>
                  </div> */}
                </div>
                {historyData.balanceSync.pendingPaymentCount > 0 && (
                  <div className="mt-2 text-center">
                    <p className="text-xs text-blue-600">
                      Last adjustment: {formatTimestampIST(historyData.balanceSync.lastAdjustmentAt)}
                    </p>
                  </div>
                )}
              </div>
            )}

            {lastRefreshed && (
              <div className="mt-3 pt-3 border-t text-center">
                <p className="text-xs text-gray-500">
                  Last updated: {formatTimestampIST(lastRefreshed.toISOString())}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Payment List */}
      <div className="p-4">
        {payments.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">💳</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No Payment History
            </h3>
            <p className="text-gray-600 mb-4">
              You haven't made any payments yet.
            </p>
            {/* <div className="flex justify-center">
              <Button
                onClick={() => setActiveView('dashboard')}
                variant="primary"
              >
                Make Your First Payment
              </Button>
            </div> */}
          </div>
        ) : (
          <>
            <div className="space-y-4 mb-6">
              {payments.map((payment) => (
                <PaymentCard key={payment.id} payment={payment} />
              ))}
            </div>

            {/* Load More */}
            {historyData && payments.length < historyData.totalTransactions && (
              <div className="text-center">
                <Button
                  onClick={handleLoadMore}
                  variant="outline"
                  loading={isLoading}
                >
                  Load More Payments
                </Button>
                <p className="text-xs text-gray-500 mt-2">
                  Showing {payments.length} of {historyData.totalTransactions} transactions
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};